<script setup>
/**
 * The lesson, as a letter on cream paper (community-lesson DESIGN.md L2;
 * redone 2026-10-28, lesson-redo DESIGN.md F, H1, I; rev 10: the plate left).
 *
 * 1. The head (`.pl-top`), one block of two parts:
 *    - the plate: the picture framed (a 12px radius, a gold hairline, a soft
 *      shadow), the Mystic's square mark on its bottom-left corner. No
 *      caption under it (picture-caption rev 1, 2026-10-30): the picture is a
 *      button (PictureOpen: the expand mark top-right, a glint, the zoom
 *      cursor) that opens it full size with its caption over the picture's
 *      foot (PictureView). Cards-only: the plum table with the cards fanned, same
 *      slot, same mark. Astrology: the night sky, its stars lit as the
 *      placements turn. Neither enlarges.
 *    - the text block: the topic (small caps), *Someone asked:* “…”, the
 *      lesson in large italic as the h1, and (with `byline`) ◯ Shared by …
 *      (seekers are circles).
 *    Wide (container ≥ 760px): two balanced columns, the plate LEFT at about
 *    36% of the head in a 4:5 crop, its height following the text block's
 *    (measured into `--pl-text-h`) so neither towers; the text block centred
 *    against it. Phone: one column, the picture capped on top, centred words.
 * 2. Their four cards, dealt (LessonStory).
 *
 * The default slot (the response, the ending) sits under the cards. `flush`:
 * no radius or shadow of its own (the lesson sheet's paper is the sheet).
 * `mark`: the Mystic's mark (off where opening a Mystic would leave a modal).
 * Nothing but the lesson is ever handed to this component.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  captionOf, creditAvatar, creditName, creditSeeker, hasPicture, isAstrology, leadIn, lessonCards, mysticOf, openCreditSeeker,
} from '@/utils/lesson'
import { topicLabel } from '@/utils/community-lessons'
import MysticMark from './MysticMark.vue'
import LessonStory from './LessonStory.vue'
import PictureOpen from './PictureOpen.vue'
import '@/assets/fonts/letter/letter-fonts.css'

const props = defineProps({
  lesson: { type: Object, required: true },
  // "Shared by …" under the lesson (the lesson page; not a seeker's own profile).
  byline: { type: Boolean, default: false },
  flush: { type: Boolean, default: false },
  mark: { type: Boolean, default: true },
})
const picFailed = ref(false)
watch(() => props.lesson && props.lesson.image_url, () => { picFailed.value = false })
const astro = computed(() => isAstrology(props.lesson))
const showPic = computed(() => !astro.value && hasPicture(props.lesson) && !picFailed.value)
const fanCards = computed(() => lessonCards(props.lesson).slice(0, 5))
const failedArt = ref({})
watch(() => props.lesson, () => { failedArt.value = {} })
// The caption (a turn, or an old title) shows only in the opened picture.
const caption = computed(() => (astro.value ? '' : captionOf(props.lesson)))
const topic = computed(() => {
  const t = String((props.lesson && props.lesson.topic_label) || topicLabel(props.lesson && props.lesson.topic) || '').trim() || 'A lesson'
  return astro.value ? `${t} · Astrology` : t
})
const lead = computed(() => leadIn(props.lesson))
const star = computed(() => String((props.lesson && props.lesson.lesson) || '').trim())
const mystic = computed(() => mysticOf(props.lesson))
const who = computed(() => creditName(props.lesson))
const named = computed(() => !!creditSeeker(props.lesson))
const avatar = computed(() => creditAvatar(props.lesson))
const avFailed = ref(false)
watch(avatar, () => { avFailed.value = false })
const initial = computed(() => (who.value || '✦').trim().charAt(0).toUpperCase())

// The night sky's stars light with the story.
const SKY = [[22, 66], [42, 34], [64, 52], [80, 24]]
const DUST = Array.from({ length: 40 }, (_, k) => ({ x: (k * 37) % 100, y: (k * 53) % 100, r: k % 3 ? 0.25 : 0.45, o: 0.25 + (k % 4) * 0.12 }))
const glyphs = computed(() => ((props.lesson && props.lesson.placements) || []).map((p) => (p && p.glyph) || '✦'))
const turned = ref(0)

// The plate's height follows the text block's (wide only; the CSS ignores it on a phone).
const headEl = ref(null)
const textH = ref(0)
let ro = null
onMounted(() => {
  if (typeof window === 'undefined' || !window.ResizeObserver || !headEl.value) return
  ro = new window.ResizeObserver((entries) => {
    const e = entries && entries[0]
    if (!e) return
    const box = e.borderBoxSize && (Array.isArray(e.borderBoxSize) ? e.borderBoxSize[0] : e.borderBoxSize)
    const h = Math.round((box && box.blockSize) || (e.contentRect && e.contentRect.height) || 0)
    if (h > 0 && Math.abs(h - textH.value) > 1) textH.value = h
  })
  ro.observe(headEl.value)
})
onBeforeUnmount(() => { if (ro) ro.disconnect(); ro = null })
const topStyle = computed(() => (textH.value ? { '--pl-text-h': `${textH.value}px` } : null))

// The picture opens (PictureOpen); a new lesson is a new picture, closed.
const picKey = computed(() => (props.lesson && props.lesson.short_uuid) || '')
</script>

<template>
  <article class="pl" :class="{ flush }" data-testid="public-letter">
    <div class="pl-top" data-testid="lesson-head" :style="topStyle">
      <figure class="pl-fig">
        <div class="pl-hero plate" :class="{ pic: showPic, cards: !showPic && !astro, sky: astro }">
          <PictureOpen v-if="showPic" :key="picKey" class="zoom" data-testid="picture-enlarge" verb="Enlarge" :src="lesson.image_url" :caption="caption"
                       img-testid="public-letter-image" @error="picFailed = true" />
          <svg v-else-if="astro" class="sky-svg" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
            <defs><radialGradient id="pl-glow"><stop offset="0" stop-color="#ffe9b8" /><stop offset="1" stop-color="#ffe9b8" stop-opacity="0" /></radialGradient></defs>
            <circle v-for="(d, k) in DUST" :key="`d${k}`" :cx="d.x" :cy="d.y" :r="d.r" fill="#cbb8ef" :opacity="d.o" />
            <line v-for="(p, i) in SKY.slice(1)" :key="`l${i}`" class="sk-l" :class="{ on: i + 1 < turned }"
                  :x1="SKY[i][0]" :y1="SKY[i][1]" :x2="p[0]" :y2="p[1]" />
            <g v-for="(p, i) in SKY.slice(0, Math.max(glyphs.length, 1))" :key="`s${i}`" class="sk-s" :class="{ on: i < turned }">
              <circle :cx="p[0]" :cy="p[1]" r="7" fill="url(#pl-glow)" class="halo" />
              <circle :cx="p[0]" :cy="p[1]" r="1.6" class="core" />
              <text :x="p[0]" :y="p[1] + 9" class="gl">{{ glyphs[i] }}</text>
            </g>
          </svg>
          <div v-else class="fan" aria-hidden="true" data-testid="public-letter-fan">
            <span v-for="(c, i) in fanCards" :key="i" class="c">
              <img v-if="c.image_url && !failedArt[i]" :src="c.image_url" alt="" :class="{ rev: c.reversed }"
                   @error="failedArt = { ...failedArt, [i]: true }" />
            </span>
          </div>
          <MysticMark v-if="mark" :mystic="mystic" size="window" />
        </div>
      </figure>

      <header ref="headEl" class="pl-head">
        <p class="pl-eb" data-testid="lesson-topic">{{ topic }}</p>
        <p v-if="lead.text" class="pl-li" data-testid="lesson-lead"><i v-if="lead.key" class="li-k">{{ lead.key }}</i>{{ lead.key ? ' ' : '' }}{{ lead.text }}</p>
        <h1 class="pl-ls" data-testid="lesson-headline">{{ star }}</h1>
        <p v-if="byline" class="pl-by">
          <span v-if="named" class="pl-av" data-testid="lesson-credit-avatar" aria-hidden="true">
            <img v-if="avatar && !avFailed" :src="avatar" alt="" @error="avFailed = true" />
            <span v-else>{{ initial }}</span>
          </span>
          <span data-testid="lesson-credit">Shared by <button v-if="named" type="button" class="pl-who" data-testid="lesson-credit-link"
              @click.stop="openCreditSeeker(lesson)">{{ who }}</button><template v-else>{{ who }}</template></span>
        </p>
      </header>
    </div>

    <LessonStory :lesson="lesson" @turned="turned = $event" />
    <slot />
  </article>
</template>

<style scoped>
.pl {
  --paper: #f4ecdc; --gold: #c9a94e; --gold-dk: #8a6a2a; --ink: #2a1845; --ink2: #3d2f55; --plum: #2a1845; --muted: #7a6a8f; --amethyst: #4e2776;
  --display: 'Letter Display', 'Cinzel', Georgia, serif; --serif: 'Letter Serif', 'Cormorant Garamond', Georgia, serif;
  position: relative; background: var(--paper); color: var(--ink); overflow: hidden; min-width: 0; text-align: center;
  border-radius: 18px; box-shadow: 0 30px 60px -30px rgba(0, 0, 0, .9);
  container: pl / inline-size;
}
.pl.flush { border-radius: 0; box-shadow: none; overflow: visible; }
.pl *, .pl *::before, .pl *::after { box-sizing: border-box; }
.pl img { display: block; }

/* 1. The head. Phone: the picture framed and capped on top, the words centred under it. */
.pl-fig { margin: 0; padding: 22px 22px 0; }
.pl-hero {
  position: relative; margin: 0 auto; width: 100%; max-width: 760px; aspect-ratio: 16 / 9; border-radius: 12px; overflow: hidden;
  background: var(--plum); border: 1px solid rgba(201, 169, 78, .55); box-shadow: 0 24px 40px -26px rgba(42, 24, 69, .75);
}
.pl-hero .zoom { position: absolute; inset: 0; }

/* Cards-only: the plum table, the cards fanned. */
.fan { position: relative; height: 100%; display: flex; align-items: center; justify-content: center; --cw: 74px; --ov: -.06;
  background: radial-gradient(70% 90% at 50% 55%, #4b2a7a 0%, #2a1548 55%, #160a2b 100%); overflow: hidden; }
.fan::after { content: ''; position: absolute; left: 12%; right: 12%; bottom: 14%; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(231, 201, 131, .35), transparent); }
.fan .c { position: relative; z-index: 1; flex: none; width: var(--cw); aspect-ratio: 2 / 3; border-radius: calc(var(--cw) * .07); overflow: hidden;
  border: 1px solid rgba(231, 201, 131, .85); background: var(--plum); box-shadow: 0 14px 22px -10px rgba(0, 0, 0, .9);
  margin: 0 calc(var(--cw) * var(--ov)); transform: translateY(var(--y, 0)) rotate(var(--r, 0deg)); }
.fan .c img { width: 100%; height: 100%; object-fit: cover; }
.fan .c img.rev { transform: rotate(180deg); }
.fan .c:nth-child(1) { --r: -8deg; --y: 7%; } .fan .c:nth-child(2) { --r: -2.5deg; }
.fan .c:nth-child(3) { --r: 2.5deg; } .fan .c:nth-child(4) { --r: 8deg; --y: 7%; } .fan .c:nth-child(5) { --r: 12deg; --y: 14%; }

/* Astrology: the night sky. */
.pl-hero.sky { background: radial-gradient(90% 90% at 50% 40%, #241046, #0d0520); }
.sky-svg { width: 100%; height: 100%; display: block; }
.sk-l { stroke: rgba(231, 201, 131, 0); stroke-width: .35; transition: stroke .8s ease .3s; }
.sk-l.on { stroke: rgba(231, 201, 131, .65); }
.sk-s .core { fill: #6f5d93; transition: fill .6s ease; }
.sk-s .halo { opacity: 0; transition: opacity .8s ease; }
.sk-s.on .core { fill: #ffe9b8; } .sk-s.on .halo { opacity: 1; }
.sk-s .gl { font: 5px var(--serif); fill: #8e7fae; text-anchor: middle; transition: fill .6s; }
.sk-s.on .gl { fill: #f0d9a3; }

/* The text block (centred on a phone; left-aligned beside the plate when wide). */
.pl-head { margin: 0 auto; padding: 22px 22px 0; max-width: 720px; }
.pl-eb { margin: 0 0 10px; font: 600 10.5px/1.5 var(--display); letter-spacing: .18em; text-transform: uppercase; color: var(--gold-dk); overflow-wrap: anywhere; }
.pl-li { margin: 0 0 12px; font: 500 19px/1.35 var(--serif); color: var(--ink2); text-wrap: pretty; overflow-wrap: anywhere; }
.li-k { font-style: italic; color: var(--muted); margin-right: .2em; }
.pl-ls { margin: 0; font: italic 600 34px/1.08 var(--serif); color: var(--ink); text-wrap: balance; letter-spacing: -.005em; overflow-wrap: anywhere; }
.pl-by { display: flex; align-items: center; justify-content: center; gap: 8px; margin: 18px 0 0; font: italic 500 15px var(--serif); color: var(--muted); min-width: 0; }
.pl-av { width: 22px; height: 22px; border-radius: 50%; overflow: hidden; flex: none; display: grid; place-items: center; background: var(--plum); border: 1px solid var(--gold);
  font: normal 600 10px/1 var(--display); color: var(--gold); }
.pl-av img { width: 100%; height: 100%; object-fit: cover; }
.pl-who {
  display: inline-flex; align-items: center; min-height: 44px; margin: -12px 0; padding: 0; border: 0; background: none; cursor: pointer;
  font: inherit; color: var(--amethyst); text-decoration: underline; text-decoration-color: rgba(78, 39, 118, .45); text-underline-offset: 3px;
}
.pl-who:hover { text-decoration-color: currentColor; }
.pl-who:focus-visible { outline: 2px solid #e7c983; outline-offset: 2px; border-radius: 4px; }

@container pl (min-width: 760px) {
  /* Rev 10: two balanced columns, the plate LEFT (4:5, about 36% of the head), its height following the
     text block's (--pl-text-h, measured) so neither towers; the words centred against the plate; the
     caption under the plate (a subgrid row, so it never moves the words). */
  .pl-top {
    --head-w: min(800px, 100cqi - 112px);
    --plate-w: clamp(212px, calc((var(--pl-text-h, 300px) + 12px) * .8), calc(var(--head-w) * .38));
    --text-w: min(400px, var(--head-w) - var(--plate-w) - 52px);
    display: grid; grid-template-columns: var(--plate-w) var(--text-w); grid-template-rows: auto auto; column-gap: 52px;
    width: calc(var(--plate-w) + 52px + var(--text-w)); margin: 0 auto; padding: 44px 0 0;
  }
  .pl-fig { grid-column: 1; grid-row: 1 / span 2; display: grid; grid-template-rows: subgrid; padding: 0; min-width: 0; }
  .pl-hero { max-width: none; aspect-ratio: 4 / 5; }
  .pl-head { grid-column: 2; grid-row: 1; align-self: center; margin: 0; padding: 0; max-width: none; text-align: left; }
  .pl-by { justify-content: flex-start; }
  .fan { --cw: 62px; --ov: -.2; }
  .pl-eb { font-size: 11.5px; }
  .pl-li { margin-bottom: 14px; font-size: 21px; }
  .pl-ls { font-size: 44px; line-height: 1.05; }
  .pl-by { margin-top: 20px; }
}
@container pl (max-width: 359px) {
  .pl-fig, .pl-head { padding-left: 16px; padding-right: 16px; }
  .pl-ls { font-size: 30px; }
}
@media (prefers-reduced-motion: reduce) { .sk-l, .sk-s .core, .sk-s .halo, .sk-s .gl { transition: none; } }
</style>
